import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Button from '../common/Button';

const PhilosophicalCompass = ({ onContinue }) => {
  const [learningStyle, setLearningStyle] = useState(null);
  const [interests, setInterests] = useState([]);
  const [lifeGoal, setLifeGoal] = useState(null);

  const handleInterestToggle = (interest) => {
    if (interests.includes(interest)) {
      setInterests(interests.filter(i => i !== interest));
    } else {
      setInterests([...interests, interest]);
    }
  };

  const handleContinue = () => {
    if (learningStyle && interests.length > 0 && lifeGoal) {
      onContinue({ learningStyle, interests, lifeGoal });
    }
  };

  const learningStyles = [
    { id: 'text', label: 'Reading and Writing', icon: '📝' },
    { id: 'audio', label: 'Listening and Discussion', icon: '🎧' },
    { id: 'visual', label: 'Visual and Diagrams', icon: '🖼️' },
    { id: 'interactive', label: 'Interactive and Hands-on', icon: '🤝' },
  ];

  const philosophicalInterests = [
    { id: 'ethics', label: 'Ethics & Morality' },
    { id: 'politics', label: 'Politics & Governance' },
    { id: 'metaphysics', label: 'Metaphysics & Reality' },
    { id: 'epistemology', label: 'Knowledge & Truth' },
    { id: 'aesthetics', label: 'Beauty & Art' },
    { id: 'logic', label: 'Logic & Reasoning' },
  ];

  const lifeGoals = [
    { id: 'knowledge', label: 'Pursuing Knowledge', description: 'Understanding the world and its workings' },
    { id: 'virtue', label: 'Cultivating Virtue', description: 'Developing excellence of character' },
    { id: 'happiness', label: 'Finding Happiness', description: 'Achieving fulfillment and well-being' },
    { id: 'purpose', label: 'Discovering Purpose', description: 'Finding meaning in your existence' },
  ];

  const SelectionCard = ({ selected, onClick, children }) => (
    <div
      className={`p-4 rounded-lg cursor-pointer transition-all duration-200 border ${
        selected
          ? 'border-oliveGold bg-oliveGold/10 ring-1 ring-oliveGold/50'
          : 'border-white/10 bg-white/5 hover:bg-white/8 hover:border-white/20'
      }`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      {children}
    </div>
  );

  return (
    <motion.div
      className="max-w-3xl mx-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <div className="text-center mb-8">
        <h2 className="text-3xl font-serif font-bold text-white mb-4">
          Your Philosophical Compass
        </h2>
        <p className="text-lg text-white/60">
          Let's personalize your journey through ancient wisdom.
        </p>
      </div>

      {/* Learning Style */}
      <div className="mb-10">
        <h3 className="text-xl font-serif font-semibold text-oliveGold/80 mb-4">
          How do you prefer to learn?
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {learningStyles.map((style, index) => (
            <motion.div
              key={style.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
            >
              <SelectionCard
                selected={learningStyle === style.id}
                onClick={() => setLearningStyle(style.id)}
              >
                <div className="flex items-center">
                  <span className="text-2xl mr-3">{style.icon}</span>
                  <span className={`text-base ${learningStyle === style.id ? 'text-oliveGold' : 'text-white/80'}`}>
                    {style.label}
                  </span>
                </div>
              </SelectionCard>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Interests */}
      <div className="mb-10">
        <h3 className="text-xl font-serif font-semibold text-oliveGold/80 mb-4">
          Areas of interest <span className="text-white/30 text-sm font-sans">(select at least one)</span>
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {philosophicalInterests.map((interest, index) => (
            <motion.div
              key={interest.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 + index * 0.08, duration: 0.5 }}
            >
              <SelectionCard
                selected={interests.includes(interest.id)}
                onClick={() => handleInterestToggle(interest.id)}
              >
                <div className="text-center py-1">
                  <span className={`text-sm ${interests.includes(interest.id) ? 'text-oliveGold' : 'text-white/70'}`}>
                    {interest.label}
                  </span>
                </div>
              </SelectionCard>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Life Goals */}
      <div className="mb-10">
        <h3 className="text-xl font-serif font-semibold text-oliveGold/80 mb-4">
          What is your current life focus?
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {lifeGoals.map((goal, index) => (
            <motion.div
              key={goal.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.7 + index * 0.1, duration: 0.5 }}
            >
              <SelectionCard
                selected={lifeGoal === goal.id}
                onClick={() => setLifeGoal(goal.id)}
              >
                <div className="p-1">
                  <span className={`text-base block mb-1 ${lifeGoal === goal.id ? 'text-oliveGold' : 'text-white/80'}`}>
                    {goal.label}
                  </span>
                  <p className="text-sm text-white/40">{goal.description}</p>
                </div>
              </SelectionCard>
            </motion.div>
          ))}
        </div>
      </div>

      <div className="mt-8 text-center">
        <Button
          onClick={handleContinue}
          disabled={!learningStyle || interests.length === 0 || !lifeGoal}
          size="lg"
        >
          Continue to Personalization
        </Button>
      </div>
    </motion.div>
  );
};

export default PhilosophicalCompass;
